import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Phone,
  Award,
  Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SEO from '../components/SEO';
import CustomElements from '../components/CustomElements';
import ReviewsSection from '../components/ReviewsSection';
import NextLevelCta from '../components/NextLevelCta';
import PartnerNetworkBanner from '../components/PartnerNetworkBanner';
import { VehicleSlideshow } from '../components/landing/VehicleSlideshow';
import { HeroBackground } from '../components/HeroBackground';
import { resolveImageUrl } from '../lib/resolveImageUrl';
import { cachedGet } from '../lib/apiCache';
import { useLocations } from '../lib/locations';
import { LOCATION_PAGES } from '../seo/seoLandingConfig';
import {
  HERO_SIZE_CLASS_MAP, DEFAULT_HERO_BUTTONS_ORDER,
  isHeroElementVisible, heroElementSize, heroElement,
} from '../components/hero/heroLayoutDefaults';

const HERO_BG = "/branding/hero-bg.png";
const ONE_WORLD_LOGO = "/branding/oneworld-logo.png";
const BMW_CARE_IMAGE = "https://images.unsplash.com/photo-1667328625755-f5f51952d024?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxCTVclMjBsdXh1cnklMjBpbnRlcmlvciUyMGFtYmllbnQlMjBsaWdodGluZyUyMGJsdWUlMjBjbGVhbnxlbnwwfHx8fDE3NzU1NzM5MDN8MA&ixlib=rb-4.1.0&q=85";
const SERVICE_IMAGE = "https://images.unsplash.com/photo-1754782385358-7d4f232ccb6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxCTVclMjBNJTIwaGlnaCUyMGdsb3NzJTIwZGFyayUyMHN0dWRpb3xlbnwwfHx8fDE3NzUzMjY1ODd8MA&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const CHROBE_LOGO = "/branding/chrobe-logo.png";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LandingPage = () => {
  const { getPage, settings, loaded } = useSiteSettings();
  const landingContent = getPage('landing');

  // FIX Bild-Flash beim Reload:
  // Ohne Cache würde `heroBg` beim ersten Render mit dem HARDCODED HERO_BG
  // fallback anfangen und dann kurz später auf das echte Bild wechseln.
  // → Wir cachen die zuletzt bekannten URLs in localStorage. Beim 2. und
  //   allen weiteren Aufrufen ist der User-Upload sofort da (kein Flash).
  const [cachedHero, setCachedHero] = useState(() => {
    try { return localStorage.getItem('chrobe:hero_bg') || null; } catch { return null; }
  });
  const [cachedAbout, setCachedAbout] = useState(() => {
    try { return localStorage.getItem('chrobe:about_img') || null; } catch { return null; }
  });

  const heroBg = resolveImageUrl(landingContent.hero_bg_image || cachedHero) || HERO_BG;
  const aboutImg = resolveImageUrl(landingContent.about_image || cachedAbout) || BMW_CARE_IMAGE;

  // Bei jedem Settings-Update den Cache aktualisieren — und, sobald die
  // echten Settings geladen sind (`loaded`), einen entfernten Wert auch
  // wirklich aus dem Cache loeschen statt den alten Stand weiter anzuzeigen.
  useEffect(() => {
    if (!loaded) return;
    if (landingContent.hero_bg_image) {
      if (landingContent.hero_bg_image !== cachedHero) {
        try { localStorage.setItem('chrobe:hero_bg', landingContent.hero_bg_image); } catch (e) { void e; }
        setCachedHero(landingContent.hero_bg_image);
      }
    } else if (cachedHero) {
      try { localStorage.removeItem('chrobe:hero_bg'); } catch (e) { void e; }
      setCachedHero(null);
    }
    if (landingContent.about_image) {
      if (landingContent.about_image !== cachedAbout) {
        try { localStorage.setItem('chrobe:about_img', landingContent.about_image); } catch (e) { void e; }
        setCachedAbout(landingContent.about_image);
      }
    } else if (cachedAbout) {
      try { localStorage.removeItem('chrobe:about_img'); } catch (e) { void e; }
      setCachedAbout(null);
    }
  }, [loaded, landingContent.hero_bg_image, landingContent.about_image, cachedHero, cachedAbout]);

  const locations = useLocations();
  const [liveServices, setLiveServices] = useState([]);
  const hasMaintenance = settings?.maintenance?.enabled;

  useEffect(() => {
    cachedGet(`${API_URL}/api/services`).then(d => setLiveServices(d?.slice(0, 6) || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#161313]" data-testid="landing-page">
      <SEO 
        title="Aufbereiter Saarland — Fahrzeugpflege & Detailing"
        description="Aufbereiter im Saarland: Fahrzeugaufbereitung, Keramikversiegelung, Lackpolitur & Detailing in Eppelborn, Saarbrücken, Neunkirchen, Saarlouis & Umgebung."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0a0909]">
        {/* Hero-Bild: 'cover' (Standard) skaliert proportional und schneidet
            Überstand ab — passt für Umgebungsfotos. Bilder mit fixem
            Ecken-Inhalt (z.B. Logos) brauchen 'contain' (im Master Panel
            einstellbar), sonst werden genau die Ecken abgeschnitten. */}
        <HeroBackground
          key={heroBg}
          src={heroBg}
          fit={landingContent.hero_bg_fit || 'cover'}
          posX={landingContent.hero_bg_pos_x ?? 65}
          posY={landingContent.hero_bg_pos_y ?? 50}
          className="animate-[fadeIn_.4s_ease-out]"
        />
        {/* Weicher Text-Overlay links — hält die Headline lesbar, ohne das
            Motiv zu erschlagen. */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0909]/85 via-[#0a0909]/40 to-transparent md:to-[#0a0909]/10" />
        {/* Radiale Vignette rundum — gibt dem Bild Tiefe, egal welches Motiv. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 65% 55%, transparent 40%, rgba(10,9,9,0.35) 75%, rgba(10,9,9,0.75) 100%)',
          }}
        />
        {/* Unterkante: sanfter Übergang in die nächste Sektion. */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#161313] to-transparent" />

        {(() => {
          const heroElements = landingContent.hero_elements || {};
          const heroFreePos = landingContent.hero_free_positions || {};
          const heroButtonsOrder = landingContent.hero_buttons_order || DEFAULT_HERO_BUTTONS_ORDER;
          const headingSize = heroElementSize(heroElements, 'heading');
          const descriptionSize = heroElementSize(heroElements, 'description');
          const badge = heroElement(heroElements, 'badge');
          // Der Hero-Kasten (frueher "Premium Partner / Zertifizierte Qualitaet") erscheint nur noch,
          // wenn im Master-Panel bewusst ein Text eingetragen wurde — kein Standardtext mehr.
          const hasBadge = !!(badge.title || badge.text);
          const isVisible = (key) => isHeroElementVisible(heroElements, key) && (key !== 'badge' || hasBadge);
          const isFree = (key) => !!heroFreePos[key];
          const buttonOrderKeys = heroButtonsOrder === 'secondary_first' ? ['cta_secondary', 'cta_primary'] : ['cta_primary', 'cta_secondary'];

          // Jede Content-Funktion nimmt die zu spreadenden Motion-Props
          // entgegen: im normalen Fluss `variants={fadeInUp}` (String-basierte
          // initial/animate-Vererbung vom umgebenden staggerContainer), frei
          // positioniert ein eigenständiges { initial, animate, transition }
          // (unabhängig vom Elternkontext, da außerhalb dessen Baum gerendert).
          const kickerContent = (motionProps) => (
            <motion.p {...motionProps} className="text-[#0094ff] font-semibold tracking-[0.18em] uppercase text-xs sm:text-sm mb-5">
              CHROBE One World
            </motion.p>
          );

          const onboardingContent = (motionProps) => (
            <motion.div {...motionProps} className="mb-6 max-w-lg">
              <div className="flex items-start gap-3 bg-[#0094ff]/8 border border-[#0094ff]/20 rounded-lg px-4 py-3" data-testid="onboarding-box">
                <Info className="w-5 h-5 text-[#0094ff] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white text-sm font-medium mb-1">{landingContent.onboarding_title || 'Neu bei One World?'}</p>
                  <p className="text-[#a1a1aa] text-xs leading-relaxed mb-2">
                    {landingContent.onboarding_text || 'Entdecke, wie unsere Plattform aufgebaut ist und welche Funktionen dich erwarten — von der Anmeldung bis zum fertigen Auftrag.'}
                  </p>
                  <Link to="/funktionen" className="inline-flex items-center gap-1.5 text-[#0094ff] text-xs font-semibold hover:underline" data-testid="onboarding-link">
                    {landingContent.onboarding_link_text || 'Alle Funktionen entdecken'} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </motion.div>
          );

          const headingContent = (motionProps) => (
            <motion.h1 {...motionProps} className={`${HERO_SIZE_CLASS_MAP.heading[headingSize]} mb-6`}>
              {/* Sichtbarer und barrierefrei vorgelesener Text sind identisch —
                  kein versteckter Keyword-Block mehr. Der Orts-/Keyword-Bezug
                  steht bereits sichtbar im SEO-Title/-Description oben sowie
                  im Fließtext der About-Sektion. */}
              <span className="text-white block">{landingContent.hero_title || 'Glanz, den man sieht. Schutz, den man nicht sieht.'}</span>
              <span className="text-[#0094ff] block mt-2 text-[0.62em] font-medium leading-snug tracking-normal">{landingContent.hero_title_accent || 'Fahrzeugaufbereitung im Saarland, mobil oder an unseren Standorten'}</span>
            </motion.h1>
          );

          const descriptionContent = (motionProps) => (
            <motion.p {...motionProps} className={`text-[#a1a1aa] mb-8 ${HERO_SIZE_CLASS_MAP.description[descriptionSize]}`}>
              {landingContent.hero_description || 'Innen- und Außenaufbereitung, Lackpolitur und Keramikversiegelung für Privatkunden, Autohäuser und Flotten. Vorab erhältst du ein schriftliches Festpreis-Angebot.'}
            </motion.p>
          );

          const primaryButtonInner = (
            <Link to="/termin">
              <Button size="lg" className="bg-[#0094ff] hover:bg-[#33a9ff] text-white font-semibold px-8 py-6 text-base uppercase tracking-wider" data-testid="cta-services">
                {landingContent.cta_primary || 'Termin anfragen'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          );
          const secondaryButtonInner = (
            <Link to="/einzugsgebiet">
              <Button size="lg" variant="outline" className="border-[#605e5e] text-white hover:bg-white/5 font-semibold px-8 py-6 text-base uppercase tracking-wider" data-testid="cta-kontakt">
                {landingContent.cta_secondary || 'Standorte & Einzugsgebiet'}
              </Button>
            </Link>
          );
          const buttonInner = { cta_primary: primaryButtonInner, cta_secondary: secondaryButtonInner };
          const buttonRowContent = (motionProps, keys) => (
            <motion.div {...motionProps} className="flex flex-col sm:flex-row gap-4">
              {keys.map(k => <React.Fragment key={k}>{buttonInner[k]}</React.Fragment>)}
            </motion.div>
          );

          const badgeContent = (motionProps) => (
            <motion.div {...motionProps}>
              <div className="bg-[#161313]/80 backdrop-blur-xl border border-[#605e5e]/20 rounded-xl p-8">
                <div className="w-12 h-12 rounded-lg bg-[#0094ff]/10 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-[#0094ff]" />
                </div>
                {badge.title && <h3 className="text-white text-xl font-bold mb-2">{badge.title}</h3>}
                {badge.text && <p className="text-[#a1a1aa] text-sm leading-relaxed">{badge.text}</p>}
              </div>
            </motion.div>
          );

          // "Frei" hat zwei Spielarten: nur ab Tablet frei (Default — auf dem
          // Handy zeigt dann eine normale Fluss-Kopie, nur `sm:hidden`, damit
          // dort nie ein ungeprüftes Layout überlappt) oder auch mobil frei
          // (explizit im Editor angehakt — dann keine Fluss-Kopie, das
          // schwebende Element ist auf allen Breakpoints aktiv).
          const isMobileFallback = (key) => isFree(key) && !heroFreePos[key]?.mobile;
          const flowButtonKeys = buttonOrderKeys.filter(k => isVisible(k) && (!isFree(k) || isMobileFallback(k)));

          const floatingRenderers = {
            kicker: kickerContent, onboarding: onboardingContent, heading: headingContent,
            description: descriptionContent, badge: badgeContent,
          };
          const floatingKeys = ['kicker', 'onboarding', 'heading', 'description', 'badge', 'cta_primary', 'cta_secondary']
            .filter(k => isVisible(k) && isFree(k));

          return (
            <>
              <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${hasMaintenance ? 'pt-28 sm:pt-32' : 'pt-4 sm:pt-8'} pb-20`}>
                <div className="grid lg:grid-cols-5 gap-12 items-end">
                  <motion.div className="lg:col-span-3" initial="initial" animate="animate" variants={staggerContainer}>
                    {isVisible('kicker') && (!isFree('kicker') ? kickerContent({ variants: fadeInUp }) : isMobileFallback('kicker') && <div className="sm:hidden">{kickerContent({ initial: { opacity: 0 }, animate: { opacity: 1 } })}</div>)}
                    {isVisible('heading') && (!isFree('heading') ? headingContent({ variants: fadeInUp }) : isMobileFallback('heading') && <div className="sm:hidden">{headingContent({ initial: { opacity: 0 }, animate: { opacity: 1 } })}</div>)}
                    {isVisible('description') && (!isFree('description') ? descriptionContent({ variants: fadeInUp }) : isMobileFallback('description') && <div className="sm:hidden">{descriptionContent({ initial: { opacity: 0 }, animate: { opacity: 1 } })}</div>)}
                    {flowButtonKeys.length > 0 && buttonRowContent({ variants: fadeInUp }, flowButtonKeys)}
                    {/* Bewusst NACH der Hauptbotschaft + CTA — die Hero-Kernaussage
                        soll zuerst kommen, der Plattform-Hinweis ist sekundär. */}
                    {isVisible('onboarding') && (!isFree('onboarding') ? onboardingContent({ variants: fadeInUp }) : isMobileFallback('onboarding') && <div className="sm:hidden">{onboardingContent({ initial: { opacity: 0 }, animate: { opacity: 1 } })}</div>)}
                  </motion.div>

                  {isVisible('badge') && !isFree('badge') && (
                    <div className="lg:col-span-2 hidden lg:block">
                      {badgeContent({ initial: { opacity: 0, x: 40 }, animate: { opacity: 1, x: 0 }, transition: { delay: 0.8, duration: 0.6 } })}
                    </div>
                  )}
                </div>
              </div>

              {/* Frei positionierte Elemente als absolute Geschwister der
                  Grid-Spalten — bei reinem Desktop/Tablet-Freimodus (kein
                  "auch mobil") nur ab `sm:` sichtbar, weil die Fluss-Kopie
                  oben darunter bereits die mobile Darstellung übernimmt. */}
              {floatingKeys.map((key) => {
                const pos = heroFreePos[key];
                const wrapperMotionProps = { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.5 } };
                const content = key === 'cta_primary' || key === 'cta_secondary'
                  ? buttonRowContent(wrapperMotionProps, [key])
                  : floatingRenderers[key](wrapperMotionProps);
                return (
                  <div
                    key={key}
                    className={`absolute z-20 max-w-[90vw] ${pos.mobile ? '' : 'hidden sm:block'}`}
                    style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%, -50%)' }}
                    data-testid={`hero-free-${key}`}
                  >
                    {content}
                  </div>
                );
              })}
            </>
          );
        })()}

        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
          <span className="text-[#605e5e] text-xs uppercase tracking-widest">Scroll</span>
          <motion.div className="w-px h-8 bg-[#605e5e]" animate={{ scaleY: [1, 0.5, 1] }} transition={{ repeat: Infinity, duration: 2 }} />
        </motion.div>
      </section>

      <PartnerNetworkBanner />

      <CustomElements page="landing" position="top" />

      {/* About Section */}
      <section className="py-24 sm:py-32 bg-[#161313]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
                {landingContent.about_section_title || <><span>Werterhalt beginnt</span><br /><span className="text-[#0094ff]">mit perfekter Pflege</span></>}
              </h2>
              <p className="text-[#a1a1aa] text-lg leading-relaxed mb-8">
                {landingContent.about_section_text || 'CHROBE One World bereitet Fahrzeuge im Saarland und in der Saarpfalz auf: Innen und außen, Lackkorrektur, Keramikversiegelung. Mobil bei dir oder an unseren Standorten. Du bekommst vorab ein schriftliches Festpreis-Angebot und einen festen Ansprechpartner.'}
              </p>
              <div className="flex flex-wrap gap-4">
                {['Mercedes', 'BMW', 'Audi', 'Porsche', 'VW'].map((brand) => (
                  <span 
                    key={brand}
                    className="px-4 py-2 bg-[#231f1f] border border-[#605e5e]/30 text-white text-sm font-medium"
                  >
                    {brand}
                  </span>
                ))}
                <span className="px-4 py-2 bg-[#0094ff]/10 border border-[#0094ff]/30 text-[#0094ff] text-sm font-medium">
                  + alle Marken
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src={aboutImg} 
                alt="Premium Fahrzeugpflege Innenraum" 
                className="rounded-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 sm:py-32 bg-[#231f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              {landingContent.services_section_title || 'Unsere Leistungen'}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(liveServices.length > 0 ? liveServices : [
              { name: "Außenpflege", description: "Professionelle Handwäsche, Schneeschaum-Vorwäsche und schonende Felgenreinigung.", price_from: 49 },
              { name: "Innenpflege", description: "Tiefenreinigung des Innenraums, Leder- und Kunststoffpflege.", price_from: 79 },
              { name: "Lackpolitur", description: "1- bis 3-Stufen Politur für Kratzerentfernung und Hochglanzfinish.", price_from: 249 },
              { name: "Keramikversiegelung", description: "Langzeitschutz bis zu 5 Jahre mit hydrophobem Effekt.", price_from: 599 },
              { name: "Full Detail", description: "Komplettpaket für innen und außen – dein Fahrzeug wie neu.", price_from: 399 },
              { name: "Neuwagen-Schutz", description: "Optimaler Schutz für deinen Neuwagen von Anfang an.", price_from: 899 }
            ]).map((service, index) => {
              return (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="service-card solid-card p-8 rounded-lg card-hover flex flex-col"
                >
                  {service.category && (
                    <p className="text-sm font-medium text-[#0094ff] mb-2">{service.category}</p>
                  )}
                  <h3 className="text-xl font-semibold text-white mb-3">{service.name}</h3>
                  <p className="text-[#a1a1aa] mb-4 leading-relaxed">{(service.description || '').replace(/<[^>]*>/g, '').slice(0, 100)}</p>
                  <p className="text-[#0094ff] font-semibold mt-auto pt-2">ab {service.price_from}€</p>
                </motion.div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                size="lg"
                className="bg-[#0094ff] hover:bg-[#33a9ff] text-white font-medium px-8"
              >
                {landingContent.services_section_cta || 'Alle Services ansehen'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Fahrzeug-Slideshow (optional, im Master-Panel konfigurierbar) */}
      <VehicleSlideshow config={landingContent.vehicle_slideshow} />

      {/* Wo du uns findest — mobil + zwei Standorte (Stand 11/2026). Die Standorte
          stehen als eigene Seiten (/standort/…); Orte des Einzugsgebiets nur als Text
          auf /einzugsgebiet. */}
      <section className="py-16 sm:py-20 border-y border-[#605e5e]/15 bg-[#0a0909]/50" aria-labelledby="service-area-heading" data-testid="landing-locations">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="service-area-heading" className="text-2xl sm:text-4xl font-bold text-white mb-10 leading-tight max-w-2xl">
            Wir kommen zu dir oder du zu uns.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[#605e5e]/25">
            {[
              { key: 'mobil', title: 'Mobil im Saarland', text: 'Bei dir zu Hause, im Büro oder im Autohaus. Im Einzugsgebiet ohne Anfahrtskosten, weiter entfernt gegen Aufpreis.', to: '/einzugsgebiet', cta: 'Einzugsgebiet' },
              ...LOCATION_PAGES.map((l) => {
                const loc = (locations || []).find((x) => x.kind === l.kind && l.cityMatch.test(x.city || ''));
                const soon = l.kind === 'branch' && !(loc && !loc.coming_soon);
                return {
                  key: l.slug,
                  title: l.city,
                  text: l.kind === 'partner'
                    ? 'Partnerbetrieb Drea’s Polierbude. Termin über CHROBE One World oder direkt beim Partner.'
                    : `Niederlassung Saar-Pfalz${soon ? (loc?.opening_date_de ? `, ab ${loc.opening_date_de}` : ', ab Januar 2027') : ''}.`,
                  to: `/standort/${l.slug}`,
                  cta: 'Zum Standort',
                };
              }),
            ].map((c, i) => (
              <div key={c.key} className={`py-6 md:py-8 md:px-8 first:md:pl-0 last:md:pr-0 border-b border-[#605e5e]/25 ${i > 0 ? 'md:border-l' : ''}`}>
                <h3 className="text-lg font-semibold text-white mb-2">{c.title}</h3>
                <p className="text-[#a1a1aa] text-sm leading-relaxed mb-4">{c.text}</p>
                <Link to={c.to} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0094ff] hover:text-white transition-colors">
                  {c.cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-[#0094ff]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              Termin anfragen
            </h2>
            <p className="text-white/85 text-lg mb-10 max-w-2xl mx-auto">
              Schreib uns, was dein Auto braucht. Du bekommst ein schriftliches Festpreis-Angebot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/termin">
                <Button 
                  size="lg"
                  className="bg-white text-[#0094ff] hover:bg-white/90 font-medium px-8 py-6 text-lg"
                >
                  Jetzt anfragen
                </Button>
              </Link>
              <a href="tel:068815959023">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white bg-transparent hover:bg-white/10 px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  06881/5959 023
                </Button>
              </a>
            </div>
            <p className="mt-6 text-white/85 text-sm" data-testid="service-area-business-cta">
              Du führst ein Autohaus oder einen Fuhrpark?{' '}
              <Link to="/business/register" className="font-semibold text-white underline underline-offset-4 hover:text-white/80">
                Für Autohäuser &amp; Flotten
              </Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Custom Elements */}
      <CustomElements page="landing" position="middle" />

      <CustomElements page="landing" position="bottom" />

      {/* My World CTA — replaces customer reviews */}
      <NextLevelCta />
    </div>
  );
};

export default LandingPage;
